import type {
  GetCurrentMemberFn,
  GetCurrentUserFn,
  PrimeMemberByPublicIdFn,
  PrimeUserFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import type { Team } from '@/features/iam/teams/Team'
import type { User } from '@/features/iam/users/User'
import type { JobProgressionFn } from '@/features/jobs'
import type { PlaySoundFn } from '@/features/sound'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { GetExternalUrlDelegate } from '@/features/shell/platform/types'
import type { Id } from '@/infra/id'
import type { PageActivityDescriptor } from '@/infra/page-activity'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import { toStream } from '@/utils/mobx-util'
import { type Observable, switchMap, take } from 'rxjs'
import type { Conversation } from './Conversation'
import type {
  ConversationViewModel,
  ConversationViewModelOptions,
} from './ConversationViewModel'
import type { MessageBankDialogViewModel } from './message-bank/MessageBankDialog/MessageBankDialogViewModel'
import type { Room } from './rooms/Room'
import type { RoomViewModel } from './rooms/RoomViewModel'

/**
 * Props for priming a conversation.
 */
export interface PrimeConversationProps {
  id: Id
}

/**
 * Gets or creates the conversation in the collection without fetching it.
 */
export type PrimeConversationFn = (
  props: PrimeConversationProps,
) => Conversation

/**
 * Props for priming a room.
 */
export interface PrimeRoomProps {
  id: Id
}

/**
 * Gets or creates the room in the collection without fetching it.
 */
export type PrimeRoomFn = (props: PrimeRoomProps) => Room

/**
 * Streams a loaded conversation.
 */
export type ConversationFn = (id: Id) => Observable<Conversation | undefined>

/**
 * Streams whether a user is online.
 */
export type OnlineStatusForUserFn = (userPublicId: Id) => Observable<boolean>

/**
 * Fetches the number of human messages in a conversation and stores it on the conversation.
 */
export type FetchHumanMessagesTotalCountFn = (
  conversationId: Id,
  workspaceId: Id,
) => Promise<void>

/**
 * Creates the view model for a conversation.
 *
 * @param getConversation Reactive getter for the conversation
 */
export type ConversationViewModelFactory = (
  getConversation: () => Conversation,
  team: Team | undefined,
  options: ConversationViewModelOptions,
) => ConversationViewModel

/**
 * Creates the view model for a consultation room.
 *
 * @param getRoomId Reactive getter for the room ID
 */
export type RoomViewModelFactory = (getRoomId: () => Id) => RoomViewModel

/**
 * Creates the view model for the message bank dialog.
 */
export type MessageBankDialogViewModelFactory = (
  getWorkspaceId: () => Id,
  onReplySelected: (replyContent: string) => void,
) => MessageBankDialogViewModel

/**
 * Options for {@link MessagingFeature}.
 */
export interface MessagingFeatureOptions {
  token$: Observable<string>
  pageActivity$: Observable<PageActivityDescriptor>
  userActive$: Observable<boolean>
  currentUser$: Observable<User>
  getCurrentUser: GetCurrentUserFn
  getCurrentMember: GetCurrentMemberFn
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
  getExternalUrlDelegate: GetExternalUrlDelegate
  primeWorkspace: PrimeWorkspaceFn
  primeMemberByPublicId: PrimeMemberByPublicIdFn
  primeUser: PrimeUserFn
  jobProgression: JobProgressionFn
  playSound: PlaySoundFn
}

/**
 * Conversations, messages, consultation rooms and the message bank.
 */
export type MessagingFeature = ReturnType<typeof MessagingFeature>

export function MessagingFeature(options: MessagingFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (m) => m.InternalMessagingFeature(options),
  )

  const conversation: ConversationFn = (id) =>
    internal.deferUntilLoaded((m) =>
      toStream(
        () => m.conversationStore.conversations.find((c) => c.id === id),
        true,
      ),
    )

  const fetchHumanMessagesTotalCount: FetchHumanMessagesTotalCountFn =
    internal.wrap(
      (m) => (conversationId, workspaceId) =>
        m.conversationStore.fetchHumanMessagesTotalCount(
          conversationId,
          workspaceId,
        ),
    )

  options.token$.pipe(take(1), switchMap(internal)).subscribe()

  return {
    // Internal
    internal,

    // Public
    primeConversation: internal.lazy(
      (m): PrimeConversationFn => m.conversationStore.primeConversation,
    ),
    primeRoom: internal.lazy((m): PrimeRoomFn => m.roomStore.primeRoom),
    newMessages$: internal.defer((m) => m.conversationStore.newMessages$),
    roomEvents$: internal.defer((m) => m.roomStore.events$),
    unreadJobIds$: internal.deferUntilLoaded((m) =>
      toStream(() => m.conversationStore.unreadJobIds, true),
    ),
    conversation,
    fetchHumanMessagesTotalCount,
    createConversationViewModel: internal.lazy(
      (m) => m.createConversationViewModel,
    ),
    createRoomViewModel: internal.lazy((m) => m.createRoomViewModel),
    createMessageBankDialogViewModel: internal.lazy(
      (m) => m.createMessageBankDialogViewModel,
    ),
    onlineStatusForUser: ((userPublicId) =>
      internal.defer((m) =>
        m.conversationStore.onlineStatusForUser(userPublicId),
      )) satisfies OnlineStatusForUserFn,
    totalUnreadCount$: internal.deferUntilLoaded((m) =>
      toStream(() => m.conversationStore.totalUnreadCount, true),
    ),
  }
}
